// services/volleyballService.js
class VolleyballService {
  constructor() {
    this.baseURL = 'https://v1.volleyball.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key as football
    this.debug = false;
    this.cache = {};
    
    if (!this.apiKey) {
      console.warn('🏐 Volleyball API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[Volleyball API] ${message}`, data || ''); 
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `volleyball_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);
    
    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarType: typeof process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarLength: process.env.REACT_APP_FOOTBALL_API_KEY ? process.env.REACT_APP_FOOTBALL_API_KEY.length : 0,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      throw new Error(`Volleyball API key not configured. 
Environment variable REACT_APP_FOOTBALL_API_KEY is: ${process.env.REACT_APP_FOOTBALL_API_KEY}
Available REACT_APP_ variables: ${Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}

Please:
1. Ensure your .env file is in the root directory (same level as package.json)
2. Restart your development server after adding the variable
3. Make sure the .env file contains: REACT_APP_FOOTBALL_API_KEY=your_key_here
4. Or use VolleyballService.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.volleyball.api-sports.io',
          'x-rapidapi-key': this.apiKey,
          'Content-Type': 'application/json'
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/games')) ttl = 60000; // 1 minute for live data
      if (endpoint.includes('/standings')) ttl = 600000; // 10 minutes for standings
      if (endpoint.includes('/countries') || endpoint.includes('/leagues')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('Volleyball API request failed:', error);
      throw error;
    }
  }

  // CORE ENDPOINTS

  // Get timezone list
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  // Get seasons
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // Get countries
  async getCountries(id = null, name = null, code = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (code) params.code = code;
    if (search) params.search = search;
    return await this.makeRequest('/countries', params);
  }

  // Get leagues/competitions
  async getLeagues(id = null, name = null, countryId = null, country = null, type = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (countryId) params.country_id = countryId;
    if (country) params.country = country;
    if (type) params.type = type;
    if (season) params.season = season;
    if (search) params.search = search;
    return await this.makeRequest('/leagues', params);
  }

  // Get teams
  async getTeams(id = null, name = null, countryId = null, country = null, league = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (countryId) params.country_id = countryId;
    if (country) params.country = country;
    if (league) params.league = league;
    if (season) params.season = season;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !countryId && !country && !league && !season && !search) {
      throw new Error('getTeams requires at least one parameter');
    }

    return await this.makeRequest('/teams', params);
  }

  // Get team statistics
  async getTeamStatistics(league, season, team, date = null) {
    if (!league || !season || !team) {
      throw new Error('getTeamStatistics requires league, season, and team parameters');
    }
    const params = { league, season, team };
    if (date) params.date = date;
    return await this.makeRequest('/teams/statistics', params);
  }

  // STANDINGS

  // Get standings
  async getStandings(league, season, team = null, stage = null, group = null) {
    if (!league || !season) {
      throw new Error('getStandings requires league and season parameters');
    }
    const params = { league, season };
    if (team) params.team = team;
    if (stage) params.stage = stage;
    if (group) params.group = group;
    return await this.makeRequest('/standings', params);
  }

  // Get standings stages
  async getStandingsStages(league, season) {
    if (!league || !season) {
      throw new Error('getStandingsStages requires league and season parameters');
    }
    return await this.makeRequest('/standings/stages', { league, season });
  }

  // Get standings groups
  async getStandingsGroups(league, season) {
    if (!league || !season) {
      throw new Error('getStandingsGroups requires league and season parameters');
    }
    return await this.makeRequest('/standings/groups', { league, season });
  }

  // GAMES & MATCHES

  // Get games/matches
  async getGames(id = null, date = null, league = null, season = null, team = null, timezone = null) {
    const params = {};
    if (id) params.id = id;
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (timezone) params.timezone = timezone;

    // This endpoint requires at least one parameter
    if (!id && !date && !league && !season && !team) {
      throw new Error('getGames requires at least one parameter');
    }

    return await this.makeRequest('/games', params);
  }

  // Get today's games
  async getTodayGames(timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getGames(null, today, null, null, null, timezone);
  }

  // Get head to head between teams
  async getHeadToHead(h2h, date = null, league = null, season = null, timezone = null) {
    if (!h2h) {
      throw new Error('getHeadToHead requires h2h parameter (team1-team2)');
    }
    const params = { h2h };
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (timezone) params.timezone = timezone;

    return await this.makeRequest('/games/h2h', params);
  }

  // ODDS

  // Get odds
  async getOdds(league = null, season = null, game = null, bookmaker = null, bet = null) {
    const params = {};
    if (league) params.league = league;
    if (season) params.season = season;
    if (game) params.game = game;
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds', params);
  }

  // Get available bets
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bets', params);
  }

  // Get bookmakers
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bookmakers', params);
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Get date range
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;
    
    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }

  // Get upcoming dates with labels
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      let label;
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      
      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Format time with timezone
  formatTime(dateString, timezone = 'UTC') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format date
  formatDate(dateString, timezone = 'UTC') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get full date time
  getFullDateTime(dateString, timezone = 'UTC') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get status color for games
  getStatusColor(status) {
    const statusMap = {
      'NS': 'bg-gray-100 text-gray-800',
      'S1': 'bg-green-100 text-green-800',
      'S2': 'bg-green-100 text-green-800',
      'S3': 'bg-green-100 text-green-800',
      'S4': 'bg-green-100 text-green-800',
      'S5': 'bg-green-100 text-green-800',
      'AW': 'bg-blue-100 text-blue-800',
      'POST': 'bg-yellow-100 text-yellow-800',
      'CANC': 'bg-red-100 text-red-800',
      'INTR': 'bg-orange-100 text-orange-800',
      'ABD': 'bg-red-100 text-red-800',
      'FT': 'bg-slate-100 text-slate-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const timezonesResult = await this.getTimezones();
      this.log('Timezones test result:', timezonesResult);
      
      if (timezonesResult && timezonesResult.response) {
        this.log('✅ API connection successful');
        return { success: true, timezones: timezonesResult.response.slice(0, 10) };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');
      
      // Try to get countries first
      const countries = await this.getCountries();
      this.log('Sample countries:', countries);
      
      // Try to get seasons
      const seasons = await this.getSeasons();
      this.log('Sample seasons:', seasons);
      
      // Try to get some leagues
      const leagues = await this.getLeagues();
      this.log('Sample leagues:', leagues);
      
      return {
        countries: countries.response?.slice(0, 10) || [],
        seasons: seasons.response?.slice(-5) || [],
        leagues: leagues.response?.slice(0, 10) || [],
      };
      
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_FOOTBALL_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length
    };
    
    console.log('🏐 Volleyball Service Environment Debug:', envInfo);
    return envInfo;
  }
}

// Add a global method for easy testing in console
window.VolleyballService = new VolleyballService();

export default window.VolleyballService;